import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { printInvoice, printKOT, printOrderJobs } from "@/lib/print";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Users, Trash2, ShoppingCart, Printer, ChefHat, X, Utensils, QrCode,
  Eye, Bike, PackageOpen, CalendarClock, Search, ArrowRightLeft, Save,
} from "lucide-react";
import { tableQrDataUrl } from "@/pages/ScanOrder";
import { guestHref } from "@/lib/guest";
import { publicUrl } from "@/lib/https";

/** PetPooja-style floor legend */
const FLOOR_STYLE = {
  blank: "border-neutral-300 bg-neutral-100 text-neutral-600",
  running: "border-sky-400 bg-sky-100 text-sky-900",
  printed: "border-emerald-400 bg-emerald-100 text-emerald-900",
  paid: "border-orange-400 bg-orange-100 text-orange-900",
  running_kot: "border-amber-400 bg-amber-100 text-amber-950",
  reserved: "border-violet-400 bg-violet-100 text-violet-900",
};

const FLOOR_LABEL = {
  blank: "Blank",
  running: "Running",
  printed: "Printed",
  paid: "Paid",
  running_kot: "Running KOT",
  reserved: "Reserved",
};

export default function Tables() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", seats: 4, section: "Main" });
  const [activeTable, setActiveTable] = useState(null);
  const [splits, setSplits] = useState([{ method: "cash", amount: 0 }]);
  const [qrTable, setQrTable] = useState(null);
  const [qrUrl, setQrUrl] = useState("");
  const [billSearch, setBillSearch] = useState("");
  const [layoutFilter, setLayoutFilter] = useState("all");
  const [moveMode, setMoveMode] = useState(false);
  const [moveFrom, setMoveFrom] = useState(null);
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  const { data: tables = [] } = useQuery({
    queryKey: ["tables"],
    queryFn: async () => (await api.get("/tables")).data,
    refetchInterval: 5000,
  });
  const { data: areas = [] } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => (await api.get("/areas")).data,
  });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { restaurant } = useAuth();
  const slug = settings?.slug || restaurant?.slug || "";
  const sym = settings?.currency_symbol || "₹";

  const { data: runningOrders = [] } = useQuery({
    queryKey: ["table-order", activeTable?.id],
    queryFn: async () => (await api.get(`/orders?table_id=${activeTable.id}&unpaid=true`)).data,
    enabled: !!activeTable,
  });
  const order = runningOrders[0];

  const sections = useMemo(() => {
    const fromTables = tables.map((t) => t.area_name || t.section || "Main");
    const fromAreas = areas.map((a) => a.name);
    return [...new Set([...fromAreas, ...fromTables])];
  }, [tables, areas]);

  const layouts = useMemo(() => ["all", ...sections], [sections]);

  const visibleTables = useMemo(() => {
    let list = tables;
    if (layoutFilter !== "all") {
      list = list.filter((t) => (t.area_name || t.section || "Main") === layoutFilter);
    }
    if (billSearch.trim()) {
      const q = billSearch.trim().toLowerCase();
      list = list.filter((t) =>
        String(t.open_order?.order_number || "").toLowerCase().includes(q)
        || String(t.name || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [tables, layoutFilter, billSearch]);

  const sectionsToShow = useMemo(() => {
    if (layoutFilter !== "all") return [layoutFilter];
    return [...new Set(visibleTables.map((t) => t.area_name || t.section || "Main"))];
  }, [visibleTables, layoutFilter]);

  const add = async () => {
    if (!form.name) return toast.error("Enter table name");
    try {
      await api.post("/tables", { ...form, seats: Number(form.seats), status: "available" });
      toast.success("Table added");
      setOpen(false);
      setForm({ name: "", seats: 4, section: "Main" });
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (t) => {
    try {
      await api.delete(`/tables/${t.id}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const floorOf = (t) => t.floor_status || (t.status === "occupied" ? "running" : t.status === "reserved" ? "reserved" : "blank");

  const openTable = async (t) => {
    if (moveMode) {
      if (!moveFrom) {
        if (!t.open_order || t.open_order.paid) return toast.error("Pick a table with an open order first");
        setMoveFrom(t);
        setSelectedIndexes([]);
        toast.message(`Moving from ${t.name} — tap a destination table`);
        return;
      }
      if (moveFrom.id === t.id) {
        setMoveFrom(null);
        return toast.message("Move cancelled");
      }
      try {
        if (selectedIndexes.length) {
          await api.post(`/orders/${moveFrom.open_order.id}/move-items`, {
            to_table_id: t.id,
            indexes: selectedIndexes,
          });
          toast.success(`Moved items → ${t.name}`);
        } else {
          await api.post(`/orders/${moveFrom.open_order.id}/move-table`, { to_table_id: t.id });
          toast.success(`Moved order → ${t.name}`);
        }
        setMoveFrom(null);
        setSelectedIndexes([]);
        setMoveMode(false);
        qc.invalidateQueries({ queryKey: ["tables"] });
        qc.invalidateQueries({ queryKey: ["orders"] });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Move failed");
      }
      return;
    }

    const fs = floorOf(t);
    if (fs === "blank" || fs === "reserved" || fs === "paid") {
      navigate(`/pos?table=${t.id}`);
    } else {
      setActiveTable(t);
      setSplits([{ method: "cash", amount: 0 }]);
    }
  };

  const reserveToggle = async (t, e) => {
    e.stopPropagation();
    const next = t.status === "reserved" ? "available" : "reserved";
    try {
      await api.put(`/tables/${t.id}`, {
        name: t.name, seats: t.seats, section: t.section || t.area_name || "Main", status: next,
        area_id: t.area_id, area_name: t.area_name,
      });
      toast.success(next === "reserved" ? "Table reserved" : "Reservation cleared");
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const settle = async () => {
    if (!order) return;
    const paid = splits.reduce((s, x) => s + Number(x.amount || 0), 0);
    if (Math.abs(paid - order.total) > 0.5) return toast.error("Amount must equal the bill total");
    try {
      await api.post(`/orders/${order.id}/pay`, {
        splits: splits.map((s) => ({ method: s.method, amount: Number(s.amount) })),
      });
      toast.success(`Table ${activeTable.name} settled · ${money(order.total, sym)}`);
      setActiveTable(null);
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Settle failed");
    }
  };

  const reprintInvoice = async (t, e) => {
    e?.stopPropagation?.();
    const oid = t.open_order?.id;
    if (!oid) return;
    try {
      await printOrderJobs(oid, "invoice", { settings }).catch(async () => {
        const { data } = await api.get(`/orders/${oid}`);
        printInvoice(data, settings);
        await api.post(`/orders/${oid}/mark-printed`);
      });
      toast.success("Bill printed");
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Print failed");
    }
  };

  const reprintKot = async (oid) => {
    try {
      await printOrderJobs(oid, "kot", { settings }).catch(async () => {
        const { data } = await api.get(`/orders/${oid}`);
        printKOT(data);
      });
      toast.success("KOT printed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "KOT failed");
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 lg:p-6" data-testid="table-view">
      <PageHeader title="Table View" subtitle="Floor plan · running / printed / KOT status at a glance">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={billSearch}
              onChange={(e) => setBillSearch(e.target.value)}
              placeholder="Bill No / table…"
              className="h-9 w-40 pl-8 text-sm"
              data-testid="bill-search"
            />
          </div>
          <Button
            data-testid="new-order-btn"
            className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"
            onClick={() => navigate("/pos")}
          >
            <ShoppingCart className="w-4 h-4 mr-1" /> New Order
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-table-btn"><Plus className="w-4 h-4 mr-1" /> Add Table</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Table</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="T13" data-testid="table-name" /></div>
                <div><Label>Seats</Label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} data-testid="table-seats" /></div>
                <div>
                  <Label>Section / Area</Label>
                  <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} list="table-sections" data-testid="table-section" />
                  <datalist id="table-sections">
                    {sections.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </div>
              </div>
              <DialogFooter><Button onClick={add} data-testid="save-table" className="bg-[var(--kul-primary)] text-white">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </PageHeader>

      {/* Action bar — PetPooja parity */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button variant="outline" className="h-10" onClick={() => navigate("/pos?type=delivery")} data-testid="tv-delivery">
          <Bike className="w-4 h-4 mr-1.5" /> Delivery
        </Button>
        <Button variant="outline" className="h-10" onClick={() => navigate("/pos?type=takeaway")} data-testid="tv-takeaway">
          <PackageOpen className="w-4 h-4 mr-1.5" /> Take Away
        </Button>
        <Button variant="outline" className="h-10" onClick={() => navigate("/reservations")} data-testid="tv-reservation">
          <CalendarClock className="w-4 h-4 mr-1.5" /> + Table Reservation
        </Button>
        <Button
          variant="outline"
          className="h-10"
          data-testid="tv-contactless"
          onClick={() => {
            const free = tables.find((t) => floorOf(t) === "blank");
            if (!free) return toast.error("No blank table for contactless QR");
            tableQrDataUrl(free.id, slug).then((url) => { setQrUrl(url); setQrTable(free); });
          }}
        >
          <QrCode className="w-4 h-4 mr-1.5" /> + Contactless
        </Button>
        <label className={`flex items-center gap-2 h-10 px-3 rounded-lg border text-sm cursor-pointer ${moveMode ? "border-[var(--kul-primary)] bg-[var(--kul-primary)]/10 text-[var(--kul-primary)]" : "border-neutral-200"}`}>
          <input
            type="checkbox"
            checked={moveMode}
            onChange={(e) => {
              setMoveMode(e.target.checked);
              setMoveFrom(null);
              setSelectedIndexes([]);
            }}
            className="accent-[var(--kul-primary)]"
            data-testid="move-kot-toggle"
          />
          <ArrowRightLeft className="w-4 h-4" /> Move KOT/Items
        </label>
        <select
          value={layoutFilter}
          onChange={(e) => setLayoutFilter(e.target.value)}
          className="h-10 rounded-lg border border-neutral-200 px-3 text-sm ml-auto"
          data-testid="floor-plan-select"
        >
          {layouts.map((l) => (
            <option key={l} value={l}>{l === "all" ? "Floor Plan · All" : l}</option>
          ))}
        </select>
      </div>

      {moveMode && (
        <div className="mb-4 text-sm rounded-lg border border-amber-200 bg-amber-50 text-amber-950 px-3 py-2">
          Move mode on{moveFrom ? ` · from ${moveFrom.name}${selectedIndexes.length ? ` (${selectedIndexes.length} items)` : " (whole order)"} — tap destination` : " · tap a running table to start"}.
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-5 text-xs">
        {Object.keys(FLOOR_STYLE).map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={`w-3.5 h-3.5 rounded border ${FLOOR_STYLE[s]}`} />
            <span className="text-neutral-600">{FLOOR_LABEL[s]}</span>
          </div>
        ))}
      </div>

      {sectionsToShow.map((sec) => {
        const rows = visibleTables.filter((t) => (t.area_name || t.section || "Main") === sec);
        if (!rows.length) return null;
        return (
          <div key={sec} className="mb-8">
            <h3 className="font-display font-700 text-lg mb-3">{sec}</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {rows.map((t) => {
                const fs = floorOf(t);
                const oo = t.open_order;
                return (
                  <div
                    key={t.id}
                    onClick={() => openTable(t)}
                    data-testid={`table-${t.id}`}
                    data-floor={fs}
                    className={`group relative aspect-square rounded-xl border-2 flex flex-col items-center justify-center cursor-pointer transition-transform hover:scale-[1.03] ${FLOOR_STYLE[fs] || FLOOR_STYLE.blank} ${moveFrom?.id === t.id ? "ring-2 ring-[var(--kul-primary)]" : ""}`}
                  >
                    <span className="font-display font-800 text-xl leading-none">{t.name}</span>
                    <span className="text-[10px] flex items-center gap-0.5 mt-1 opacity-80"><Users className="w-3 h-3" />{t.seats}</span>
                    {oo && !oo.paid && (
                      <span className="text-[10px] font-mono-k mt-0.5 font-semibold">{money(oo.total, sym)}</span>
                    )}
                    <span className="text-[9px] uppercase tracking-wide mt-0.5 opacity-70">{FLOOR_LABEL[fs]}</span>

                    {/* Card action icons */}
                    <div className="absolute top-1 right-1 flex gap-0.5">
                      {(fs === "running" || fs === "running_kot" || fs === "printed") && oo && (
                        <>
                          <button
                            type="button"
                            title="Reprint bill"
                            onClick={(e) => reprintInvoice(t, e)}
                            className="p-0.5 rounded bg-white/80 text-neutral-600 hover:text-[var(--kul-primary)]"
                            data-testid={`table-print-${t.id}`}
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            title="View bill"
                            onClick={(e) => { e.stopPropagation(); setActiveTable(t); }}
                            className="p-0.5 rounded bg-white/80 text-neutral-600"
                            data-testid={`table-view-${t.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {fs === "printed" && (
                        <span title="Bill printed" className="p-0.5 rounded bg-white/80 text-emerald-700">
                          <Save className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => reserveToggle(t, e)}
                      title="Reserve"
                      className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 text-[9px] px-1 rounded bg-white/80"
                    >
                      {t.status === "reserved" ? "free" : "book"}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); del(t); }}
                      className="absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-[var(--kul-danger)]"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        setQrUrl(await tableQrDataUrl(t.id, slug));
                        setQrTable(t);
                      }}
                      className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 text-[var(--kul-primary)]"
                      title="Contactless QR"
                    >
                      <QrCode className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {!visibleTables.length && (
        <p className="text-center text-neutral-400 py-16">No tables match this floor plan / search.</p>
      )}

      {/* Running bill dialog */}
      <Dialog open={!!activeTable} onOpenChange={(v) => { if (!v) { setActiveTable(null); setSelectedIndexes([]); } }}>
        <DialogContent className="max-w-md" data-testid="table-bill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Utensils className="w-5 h-5 text-[var(--kul-primary)]" />
              Table {activeTable?.name} · {FLOOR_LABEL[floorOf(activeTable || {})] || "Bill"}
            </DialogTitle>
          </DialogHeader>
          {!order ? (
            <div className="py-6 text-center text-neutral-400">
              No running order for this table.
              <Button className="mt-4 bg-[var(--kul-primary)] text-white" onClick={() => navigate(`/pos?table=${activeTable.id}`)}>Take Order</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-neutral-400 font-mono-k flex justify-between">
                <span>{order.order_number}</span>
                <span className="capitalize">{order.kitchen_status || order.status}</span>
              </div>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {order.items.map((it, i) => (
                  <label key={i} className="flex justify-between text-sm gap-2 cursor-pointer">
                    <span className="flex items-center gap-2 min-w-0">
                      {moveMode && (
                        <input
                          type="checkbox"
                          checked={selectedIndexes.includes(i)}
                          onChange={() => setSelectedIndexes((xs) => xs.includes(i) ? xs.filter((x) => x !== i) : [...xs, i])}
                        />
                      )}
                      <span className="truncate">{it.qty}× {it.name}</span>
                    </span>
                    <span className="font-mono-k text-neutral-500 shrink-0">{money(it.price * it.qty, sym)}</span>
                  </label>
                ))}
              </div>
              <div className="border-t pt-2 text-sm space-y-1">
                <div className="flex justify-between text-neutral-500"><span>Subtotal</span><span className="font-mono-k">{money(order.subtotal, sym)}</span></div>
                <div className="flex justify-between text-neutral-500"><span>GST</span><span className="font-mono-k">{money(order.tax_total, sym)}</span></div>
                <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="font-mono-k text-[var(--kul-primary)]">{money(order.total, sym)}</span></div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => reprintKot(order.id)}><ChefHat className="w-4 h-4 mr-1" />KOT</Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => reprintInvoice({ open_order: { id: order.id } })}><Printer className="w-4 h-4 mr-1" />Invoice</Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/pos?table=${activeTable.id}&order=${order.id}`)}><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
              {moveMode && (
                <p className="text-xs text-amber-800 bg-amber-50 rounded p-2">
                  Select items above (or none for whole order), close this dialog, then tap the destination table.
                </p>
              )}
              <div className="border-t pt-3 space-y-2">
                <Label>Settle payment</Label>
                {splits.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select value={s.method} onChange={(e) => setSplits(splits.map((x, xi) => xi === i ? { ...x, method: e.target.value } : x))} className="h-9 rounded-lg border px-2 text-sm flex-1" data-testid={`tbl-method-${i}`}>
                      <option value="cash">Cash</option><option value="card">Card</option><option value="upi">UPI</option><option value="edc">EDC</option>
                    </select>
                    <Input type="number" value={s.amount} onChange={(e) => setSplits(splits.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))} className="h-9 w-28 text-right" data-testid={`tbl-amount-${i}`} />
                    {splits.length > 1 && <button type="button" onClick={() => setSplits(splits.filter((_, xi) => xi !== i))}><X className="w-4 h-4 text-neutral-400" /></button>}
                  </div>
                ))}
                <div className="flex justify-between items-center">
                  <button type="button" onClick={() => setSplits([...splits, { method: "card", amount: 0 }])} className="text-sm text-[var(--kul-primary)]"><Plus className="w-3 h-3 inline" /> split</button>
                  <button type="button" onClick={() => setSplits([{ method: "cash", amount: Number(order.total.toFixed(2)) }])} className="text-xs text-neutral-500">Fill total</button>
                </div>
                <Button onClick={settle} data-testid="settle-table" className="w-full h-11 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white font-bold">Settle {money(order.total, sym)}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrTable} onOpenChange={(v) => { if (!v) setQrTable(null); }}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader><DialogTitle>Contactless · Table {qrTable?.name}</DialogTitle></DialogHeader>
          {qrUrl && <img src={qrUrl} alt="Table QR" className="mx-auto w-64 h-64" />}
          <p className="text-xs text-neutral-500">Guest scans to order and call waiter / water / bill.</p>
          <p className="text-xs break-all text-neutral-400">{typeof window !== "undefined" && qrTable ? publicUrl(guestHref(slug, `/scan/${qrTable.id}`)) : ""}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
